import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { QUESTIONS, BRANCHEN } from "@/lib/questions";
import { api } from "@/lib/api";
import { publicApiError } from "@/lib/apiErrors";

const TOTAL_STEPS = QUESTIONS.length + 2; // intro + 15 + finalize

export default function Assessment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = intro, 1..15 = questions, 16 = finalize
  const [organization, setOrganization] = useState("");
  const [branch, setBranch] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState({}); // qid -> { value, label, optionIndex }
  const [submitting, setSubmitting] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [clarification, setClarification] = useState("");

  const progress = Math.round((step / (TOTAL_STEPS - 1)) * 100);
  const currentQuestion = step >= 1 && step <= QUESTIONS.length ? QUESTIONS[step - 1] : null;
  const totalQuestionsAnswered = useMemo(() => Object.keys(answers).length, [answers]);

  const selectOption = (q, optIndex) => {
    const opt = q.options[optIndex];
    setAnswers((prev) => ({
      ...prev,
      [q.id]: { value: opt.value, label: opt.label, hint: opt.hint, optionIndex: optIndex },
    }));
  };

  const goNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!privacyAccepted) {
      toast.error("Bitte bestätigen Sie die Datenschutzerklärung, um fortzufahren.");
      return;
    }
    setSubmitting(true);
    setClarification("");
    try {
      const numericAnswers = {};
      const answerTexts = {};
      QUESTIONS.forEach((q) => {
        const a = answers[q.id];
        numericAnswers[q.id] = a ? a.value : 0;
        answerTexts[q.id] = a
          ? `${q.text} — Antwort: ${a.label}${a.hint ? " (" + a.hint + ")" : ""}`
          : q.text;
      });
      const payload = {
        organization: organization || null,
        branch: branch || null,
        contact_name: contactName || null,
        email: email || null,
        answers: numericAnswers,
        answer_texts: answerTexts,
      };
      const { data } = await api.post("/assessments", payload);
      toast.success("Ihre Einschätzung ist fertig.");
      navigate(`/ergebnis/${data.id}`);
    } catch (e) {
      const problem = publicApiError(e);
      if (problem.kind === "clarification") setClarification(problem.message);
      else toast.error(problem.message);
      setSubmitting(false);
    }
  };

  const renderIntro = () => (
    <motion.div
      key="intro"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5, ease: [0.16, 0.84, 0.44, 1] }}
      className="max-w-2xl"
    >
      <div className="kk-overline mb-6">Vor dem Kompass</div>
      <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-[var(--kk-anthracite)] leading-[1.1]">
        Bevor wir starten – ein paar ruhige Worte zu Ihnen.
      </h1>
      <p className="mt-6 text-base sm:text-lg text-[var(--kk-anthracite)]/70 leading-relaxed">
        Diese Angaben sind freiwillig. Sie helfen uns, Ihren Kurzbericht in den
        richtigen Kontext zu setzen. Wenn Sie nichts angeben möchten, lassen
        Sie die Felder einfach leer.
      </p>

      <div className="mt-10 grid sm:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--kk-anthracite)]/60 font-semibold">Organisation</span>
          <input
            type="text"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            placeholder="z. B. Stadtwerke Musterstadt"
            data-testid="intro-organization-input"
            className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[var(--kk-bluegreen)] outline-none py-2 text-[var(--kk-anthracite)] placeholder-[var(--kk-anthracite)]/30 transition-colors"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--kk-anthracite)]/60 font-semibold">Branche</span>
          <select
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            data-testid="intro-branch-select"
            className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[var(--kk-bluegreen)] outline-none py-2 text-[var(--kk-anthracite)] transition-colors"
          >
            <option value="">– bitte wählen –</option>
            {BRANCHEN.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
      </div>

      <div className="mt-12 flex items-center gap-3">
        <button
          type="button"
          onClick={goNext}
          className="kk-btn-primary"
          data-testid="intro-continue-btn"
        >
          Mit der ersten Frage beginnen
          <ArrowRight className="w-4 h-4" />
        </button>
        <span className="text-xs text-[var(--kk-anthracite)]/50">~ 7 Minuten</span>
      </div>
    </motion.div>
  );

  const renderQuestion = (q, idx) => {
    const answered = answers[q.id];
    const canContinue = !!answered;
    return (
      <motion.div
        key={q.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.45, ease: [0.16, 0.84, 0.44, 1] }}
        className="max-w-2xl"
        data-testid={`question-${q.id}`}
      >
        <div className="kk-overline mb-6">
          Frage {String(idx + 1).padStart(2, "0")} <span className="opacity-50">/ {String(QUESTIONS.length).padStart(2, "0")}</span>
        </div>
        {q.intro && (
          <p className="text-sm text-[var(--kk-bluegreen)] mb-3 italic" data-testid={`question-intro-${q.id}`}>
            {q.intro}
          </p>
        )}
        <h2 className="text-2xl sm:text-3xl leading-snug font-medium text-[var(--kk-anthracite)] tracking-tight">
          {q.text}
        </h2>

        <div className="mt-10 space-y-3">
          {q.options.map((opt, i) => {
            const selected = answered?.optionIndex === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => selectOption(q, i)}
                data-selected={selected}
                data-testid={`option-${q.id}-${i}`}
                className="kk-option"
              >
                <span className="kk-option-marker">{String.fromCharCode(65 + i)}</span>
                <span className="flex-1">
                  <span className="block text-base font-medium text-[var(--kk-anthracite)]">{opt.label}</span>
                  {opt.hint && <span className="block text-sm text-[var(--kk-anthracite)]/55 mt-1">{opt.hint}</span>}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-12 flex items-center justify-between">
          <button
            type="button"
            onClick={goBack}
            className="text-sm text-[var(--kk-anthracite)]/60 hover:text-[var(--kk-anthracite)] inline-flex items-center gap-2"
            data-testid={`back-btn-${q.id}`}
          >
            <ArrowLeft className="w-4 h-4" /> Zurück
          </button>
          <button
            type="button"
            disabled={!canContinue}
            onClick={goNext}
            className="kk-btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`continue-btn-${q.id}`}
          >
            Weiter
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {!canContinue && (
          <p className="mt-5 text-sm text-[var(--kk-anthracite)]/70 leading-relaxed" data-testid={`missing-answer-${q.id}`} role="status">
            Für eine verlässliche Auswertung fehlt uns hier noch eine Angabe. Bitte wählen Sie eine Antwort aus oder gehen Sie zurück, um Ihre Angaben zu prüfen.
          </p>
        )}
      </motion.div>
    );
  };

  const renderFinalize = () => (
    <motion.div
      key="finalize"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5 }}
      className="max-w-2xl"
    >
      <div className="kk-overline mb-6">Letzter Schritt</div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--kk-anthracite)] leading-[1.1]">
        Wohin dürfen wir Ihre individuelle Auswertung senden?
      </h2>
      <div className="mt-6 space-y-4 text-base text-[var(--kk-anthracite)]/75 leading-relaxed max-w-2xl">
        <p>Ihre individuelle Auswertung ist erstellt.</p>
        <p>
          Die vollständige Auswertung mit konkreten Hinweisen zu Wissen,
          Prozessen und Entlastung stellen wir Ihnen gerne als PDF zur
          Verfügung.
        </p>
        <p>
          Bitte geben Sie Ihre E-Mail-Adresse an, damit wir Ihnen die
          Auswertung zusenden können.
        </p>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--kk-anthracite)]/60 font-semibold">Name (optional)</span>
          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Vor- und Nachname"
            data-testid="final-name-input"
            className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[var(--kk-bluegreen)] outline-none py-2 text-[var(--kk-anthracite)] placeholder-[var(--kk-anthracite)]/30 transition-colors"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--kk-anthracite)]/60 font-semibold">E-Mail (optional)</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@beispiel.de"
            data-testid="final-email-input"
            className="mt-2 w-full bg-transparent border-b border-[hsl(var(--border))] focus:border-[var(--kk-bluegreen)] outline-none py-2 text-[var(--kk-anthracite)] placeholder-[var(--kk-anthracite)]/30 transition-colors"
          />
        </label>
      </div>

      {clarification && (
        <div className="mt-8 border border-[hsl(var(--border))] bg-[var(--kk-offwhite)] p-6" data-testid="clarification-case" role="status">
          <div className="font-semibold text-[var(--kk-anthracite)]">Bitte prüfen Sie Ihre Angaben</div>
          <p className="mt-2 text-sm text-[var(--kk-anthracite)]/70 leading-relaxed">{clarification}</p>
          <button type="button" className="kk-btn-ghost mt-5" onClick={() => setStep(1)} data-testid="clarification-review-btn">
            Angaben noch einmal prüfen
          </button>
        </div>
      )}

      {/* DSGVO – Pflicht-Checkbox + Hinweis */}
      <div className="mt-10 border-t border-[hsl(var(--border))] pt-6">
        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="privacy-consent-label">
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            data-testid="privacy-consent-checkbox"
            className="mt-1 w-4 h-4 accent-[var(--kk-bluegreen)] shrink-0"
            required
          />
          <span className="text-sm text-[var(--kk-anthracite)]/85 leading-relaxed">
            Ich habe die{" "}
            <a
              href="https://www.one-a-com.net/datenschutz"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-[var(--kk-bluegreen)]/50 underline-offset-4 hover:text-[var(--kk-anthracite)] hover:decoration-[var(--kk-bluegreen)]"
              data-testid="privacy-policy-link"
            >
              Datenschutzerklärung
            </a>{" "}
            gelesen und stimme der Verarbeitung meiner Daten zur Erstellung
            und Zusendung meiner individuellen Auswertung zu.
          </span>
        </label>
        <p className="mt-3 text-xs text-[var(--kk-anthracite)]/55 leading-relaxed max-w-xl" data-testid="privacy-hint">
          Ihre Angaben werden zur Erstellung Ihrer individuellen Auswertung, zur
          Bearbeitung Ihrer Anfrage sowie zur Dokumentation der Ergebnisse
          verarbeitet. Weitere Informationen finden Sie in unserer{" "}
          <a
            href="https://www.one-a-com.net/datenschutz"
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-[var(--kk-bluegreen)]/40 underline-offset-4 hover:text-[var(--kk-anthracite)]"
          >
            Datenschutzerklärung
          </a>.
        </p>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button type="button" onClick={goBack} className="text-sm text-[var(--kk-anthracite)]/60 hover:text-[var(--kk-anthracite)] inline-flex items-center gap-2" data-testid="final-back-btn">
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || totalQuestionsAnswered < QUESTIONS.length || !privacyAccepted}
          className="kk-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="final-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Individuelle Auswertung wird vorbereitet …
            </>
          ) : (
            <>
              Auswertung zusenden
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
      {totalQuestionsAnswered < QUESTIONS.length && (
        <p className="mt-6 text-xs text-[var(--kk-anthracite)]/50" data-testid="final-warning">
          Es sind noch {QUESTIONS.length - totalQuestionsAnswered} Frage(n) offen.
        </p>
      )}
    </motion.div>
  );

  return (
    <div className="bg-white min-h-[calc(100vh-120px)]" data-testid="assessment-page">
      {/* progress + label */}
      <div className="sticky top-[73px] z-20 bg-white/95 backdrop-blur border-b border-[hsl(var(--border))]">
        <div className="kk-progress-track">
          <div
            className="kk-progress-bar"
            style={{ width: `${progress}%` }}
            data-testid="progress-bar"
          />
        </div>
        <div className="kk-container py-3 flex justify-between items-center text-[10px] uppercase tracking-[0.22em] text-[var(--kk-anthracite)]/55 font-semibold">
          <span data-testid="progress-label">
            {step === 0
              ? "Vor dem Kompass"
              : step > QUESTIONS.length
              ? "Letzter Schritt"
              : `Schritt ${step} / ${QUESTIONS.length}`}
          </span>
          <span>{progress}%</span>
        </div>
      </div>

      <div className="kk-container py-16 sm:py-24">
        <AnimatePresence mode="wait">
          {step === 0 && renderIntro()}
          {currentQuestion && renderQuestion(currentQuestion, step - 1)}
          {step > QUESTIONS.length && renderFinalize()}
        </AnimatePresence>
      </div>
    </div>
  );
}
